import { useMemo } from 'react';
import { Button } from 'react-bootstrap';

import SelectMulti from 'app/components/Select/SelectMulti';
import Typography from 'app/components/Typography';

import { SensorConnectionStatus } from 'app/modules/inventory/bins/core/enum';
import { LOCATION_OPTIONS } from 'app/modules/inventory/items/core/constants';
import { Body, Header, Main } from 'app/modules/inventory/stock-run/components/StockRunFilter';
import { STATUS_OPTIONS } from 'app/modules/system-settings/devices/core/constant';

import useDeviceSensorFilter from '../hooks/useDeviceSensorFilter';

interface DeviceSensorFilterProps {
  openFilter: boolean;
  setIsOpenFilter: React.Dispatch<React.SetStateAction<boolean>>;
  locations: any[];
}

const DeviceSensorFilter = ({
  openFilter,
  setIsOpenFilter,
  locations,
}: DeviceSensorFilterProps) => {
  const {
    filter,
    menuIsOpen,
    handleClearAll,
    handleMenuOpen,
    handleMenuClose,
    handleSelectChange,
    handleApplyFilter,
  } = useDeviceSensorFilter(openFilter, setIsOpenFilter);

  const locationOptions = useMemo(() => {
    return LOCATION_OPTIONS(locations).filter((opt) => !filter.locationIds?.includes(opt.value));
  }, [filter.locationIds, locations]);

  const selectedLocation = (selectedValues: string[], locationsMapping: any[]) =>
    selectedValues.map((value) => ({
      label: locationsMapping.find((loc) => loc.id === Number(value))?.name || value,
      value,
    }));

  const getStatusOptions = (statuses: (SensorConnectionStatus | number | string)[]) =>
    statuses.map((status) => {
      const matched = STATUS_OPTIONS.find((opt) => String(opt.value) === String(status));
      return {
        value: String(status),
        label: matched?.label || String(status),
      };
    });

  const availableStatusOptions = useMemo(() => {
    const selectedValues = (filter.status || []).map((v) => String(v));
    return STATUS_OPTIONS.filter((opt) => !selectedValues.includes(String(opt.value)));
  }, [filter.status]);

  const selectedStatusOptions = useMemo(() => {
    return getStatusOptions(filter.status || []);
  }, [filter.status]);

  return (
    <Main>
      <Header>
        <Typography variant="heading-6">Filter</Typography>
        <Typography
          className="clear_all cursor-pointer"
          variant="body-5"
          onClick={handleClearAll}
          color="primary"
        >
          Clear all
        </Typography>
      </Header>
      <Body>
        <Typography variant="body-7" color="gray-700" className="mb-2">
          Status
        </Typography>
        <SelectMulti
          options={availableStatusOptions}
          selectedOption={selectedStatusOptions}
          handleChange={handleSelectChange('status')}
          placeholder="Select status to filter"
          menuIsOpen={menuIsOpen.status}
          onMenuClose={handleMenuClose('status')}
          openMenuOnClick={handleMenuOpen('status')}
        />

        <div className="mt-4">
          <Typography variant="body-7" color="gray-700" className="mb-2">
            Location
          </Typography>
          <SelectMulti
            options={locationOptions}
            selectedOption={selectedLocation(filter.locationIds ?? [], locations)}
            handleChange={handleSelectChange('locationIds')}
            placeholder="Select location to filter"
            menuIsOpen={menuIsOpen.locationIds}
            onMenuClose={handleMenuClose('locationIds')}
            openMenuOnClick={handleMenuOpen('locationIds')}
          />
        </div>

        <div className="d-flex justify-content-end mt-4">
          <Button variant="primary" onClick={handleApplyFilter}>
            Apply
          </Button>
        </div>
      </Body>
    </Main>
  );
};

export default DeviceSensorFilter;
